import { CheckCircleOutlineRounded, HighlightOffRounded, SendRounded } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil";
import { getProduct, RESPONSE_CHECK_CODE, RESPONSE_TYPE_SERVICE, useCouponQuery, getProductLeisure } from "../../../../../api/services";
import { FormTextField } from "../../../../../components/fields";
import { ReactQueryKey, RentalType } from "../../../../../types";
import { codeDescriptionState, isBusinessContinuityMode, isLeisureCode, stepState } from "../../../recoil";
import { IFormValues, InsertBookingStep } from "../../../types";
import { getCarGroupCode } from "../../../../../utils";

const FormCoupon = () => {

    const { t } = useTranslation('booking');

    const step = useRecoilValue(stepState);
    const businessContinuityMode = useRecoilValue(isBusinessContinuityMode);
    const [flagLeisureCode, setLeisureCode] = useRecoilState(isLeisureCode);
    const setCodeDescription = useSetRecoilState(codeDescriptionState);
    // todo salvare il type della promo da inviare nella draft
    // attualmente inviato solo se leisure

    const formContext = useFormContext<IFormValues>();

    const discountCode = useWatch<IFormValues, 'extraServices.discountCode'>({
        name: 'extraServices.discountCode'
    });
    const pickupLocation = useWatch<IFormValues, 'basicDetails.pickupLocation'>({
        name: 'basicDetails.pickupLocation'
    });
    const dropoffLocation = useWatch<IFormValues, 'basicDetails.dropoffLocation'>({
        name: 'basicDetails.dropoffLocation'
    });
    const pickupDate = useWatch<IFormValues, 'basicDetails.pickupDate'>({
        name: 'basicDetails.pickupDate'
    });

    const dropoffDate = useWatch<IFormValues, 'basicDetails.dropoffDate'>({
        name: 'basicDetails.dropoffDate'
    });

    const carGroup = useWatch<IFormValues, 'basicDetails.carGroup'>({
        name: 'basicDetails.carGroup'
    });

    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });

    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const productsQuery = useQuery({
        queryKey: [ReactQueryKey.Product, {
            segment: rentalType,
            tenant: tenant,
            dealer: dealer
        }],
        queryFn: () => getProduct({
            segment: rentalType,
            businessContinuityInsToolMode: businessContinuityMode,
            tenant: tenant as string,
            dealer: dealer as string
        }),
        enabled: false
    });

    const queryClient = useQueryClient();

    const couponQuery = useCouponQuery(
        {
            pickUpDate: pickupDate?.isValid() ? pickupDate.toISOString() : undefined,
            dropoffDate: dropoffDate?.isValid() ? dropoffDate.toISOString() : undefined,
            pickUpLocation: pickupLocation?.id,
            dropOffLocation: dropoffLocation?.id,
            promoCode: discountCode,
            carGroupCode: !!carGroup ? getCarGroupCode(carGroup) : undefined,
            productCodes: !!productsQuery?.data?.products ? productsQuery.data.products.map(elem => {return elem.code}) : undefined,
            tenant: tenant as string,
            dealer: dealer as string
        },
        {
            enabled: false,
            onSuccess: async (data) => {
                if (data?.code !== RESPONSE_CHECK_CODE.SUCCESS) {
                    formContext.setError('extraServices.discountCode', {
                        message: 'message.validation.invalid_code'
                    });
                    return;
                }
                formContext.clearErrors('extraServices.discountCode');

                formContext.setValue("extraServices.checkCoupon", data); // x validation

                setCodeDescription(data.codeServiceType);

                if(data.codeServiceType !== RESPONSE_TYPE_SERVICE.leisure){
                    setLeisureCode(false);
                    return;
                }

                queryClient.resetQueries([ReactQueryKey.RatePlan, {
                    tenant: formContext.getValues('basicDetails.tenants') as string,
                    dealer: formContext.getValues('basicDetails.dealers') as string
                }])
                queryClient.resetQueries([ReactQueryKey.TotalToBePaid, {
                    tenant: formContext.getValues('basicDetails.tenants') as string,
                    dealer: formContext.getValues('basicDetails.dealers') as string
                }])
                setLeisureCode(true);

                // reset form
                const leisureCode = formContext.getValues('extraServices.discountCode') ?? '';
                // Preserva i valori di tenant e dealer prima del reset
                const currentTenant = formContext.getValues('basicDetails.tenants');
                const currentDealer = formContext.getValues('basicDetails.dealers');
                
                formContext.resetField('basicDetails');
                formContext.resetField('extraServices');
                formContext.setValue('extraServices.discountType', 'promo');
                formContext.setValue('extraServices.discountCode', leisureCode);
                formContext.setValue('extraServices.checkCoupon', data); // Preserva la validazione del coupon
                
                formContext.setValue('basicDetails.pickupDate', pickupDate);
                formContext.setValue('basicDetails.dropoffDate', dropoffDate);
                
                // Ripristina i valori di tenant e dealer
                if (currentTenant) {
                    formContext.setValue('basicDetails.tenants', currentTenant);
                }
                if (currentDealer) {
                    formContext.setValue('basicDetails.dealers', currentDealer);
                }

                const  productList = data?.leisureResult?.productList ?? [];
                const pickUpDate = pickupDate?.isValid() ? pickupDate.toISOString() : '';
                
                await queryClient.fetchQuery({
                    queryKey: [ReactQueryKey.Product, {segment: RentalType.RETAIL, tenant: tenant as string, dealer: dealer as string}],
                    queryFn: () => getProductLeisure({leisureCode, productList, pickUpDate, tenant: tenant as string, dealer: dealer as string}),
                })
            },
            onError: () => {
                formContext.setError('extraServices.discountCode', {
                    message: 'message.validation.invalid_code'
                });
             }
        }
    );

    const handleCheck = useCallback(() => {
        couponQuery.refetch();
    }, []);
    const handleKeyDown = useCallback(() => {
        queryClient.resetQueries([ReactQueryKey.Coupon, {
            tenant: formContext.getValues('basicDetails.tenants') as string,
            dealer: formContext.getValues('basicDetails.dealers') as string
        }]);
        if (formContext.getFieldState('extraServices.discountCode').error) {
            formContext.clearErrors('extraServices.discountCode');
        }
    }, []);

    const EndAdornemnt = useMemo(() => {
        if (
            !discountCode
        ) {
            return undefined;
        }
        if (couponQuery.isFetching) {
            return <CircularProgress size={20} />
        }
        if (!couponQuery.data) {
            return (
                <InputAdornment position="end">
                    <IconButton
                        onClick={handleCheck}
                        disabled={step === InsertBookingStep.CUSTOMER_DETAILS || flagLeisureCode}
                    >
                        <SendRounded />
                    </IconButton>
                </InputAdornment>
            )
        }
        if (couponQuery.data.code !== RESPONSE_CHECK_CODE.SUCCESS) {
            return (
                <HighlightOffRounded
                    sx={{
                        fill: 'red'
                    }}
                />
            );
        }
        return (
            <CheckCircleOutlineRounded
                sx={{
                    fill: 'green'
                }}
            />
        );
    }, [
        discountCode,
        pickupDate,
        dropoffDate,
        pickupLocation,
        dropoffLocation,
        carGroup,
        couponQuery,
        productsQuery?.data?.products,
        step
    ]);

    return (
        <FormTextField<IFormValues>
            name="extraServices.discountCode"
            label={t('label.promoCouponLeisureCode') + '*'}
            disabled={couponQuery.isFetching || step === InsertBookingStep.CUSTOMER_DETAILS || flagLeisureCode}
            InputProps={{
                endAdornment: EndAdornemnt
            }}
            onKeyDown={handleKeyDown}
        />
    );
}

export default FormCoupon;
